/**
 * #976 — composes the card-annotation scope, adapters and both providers for
 * the Tracker. `PositionsRoute` in App.tsx mounts it (#976 T5); no other route does.
 *
 * - Mount: one `readScope`; both providers get the same token.
 * - Identity (F1): the auth fields that bump the session epoch (user id, org,
 *   role, signed-out) null the scope DURING RENDER, so the providers drop the
 *   old account's entries before any new-identity card renders; then it re-reads.
 * - Refused call (scope invalid / membership / unavailable target): re-read
 *   once (bursts coalesce). While a note draft is dirty the re-read waits
 *   until the editor is closed or clean, so a refused save never drops it.
 * - A refused or failed scope read sets the scope to null: every control is
 *   disabled with its reason. A refusal never triggers another read. A transport
 *   failure or 5xx gets ONE retry after `retryMs`. (The other exception: a read cut
 *   off by an auth change that left the identity unchanged, see below.)
 * - Signed out: no read at all.
 * - The transport is read once, at mount.
 *
 * Where this is mounted is the rule for who gets annotations (the org detail
 * route never mounts it, so it sends nothing).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { CardNotesProvider, useCardNotes } from '@/components/cards/cardNotes/CardNotesProvider';
import { CardSourcesProvider } from '@/components/cards/cardSources/CardSourcesProvider';
import { createCardNoteAdapter, createCardSourceAdapter, pageScopeFor } from '@/lib/cardAnnotations/adapters';
import { createCardAvailabilityChannel } from '@/lib/cardAnnotations/availability';
import { browserCardAnnotationTransport } from '@/lib/cardAnnotations/browserTransport';
import { CardAnnotationClientError, createCardAnnotationClient } from '@/lib/cardAnnotations/client';
import { watchRefusals } from '@/lib/cardAnnotations/refusals';
import type { CardAnnotationTransport, CardVenue } from '@/lib/cardAnnotations/types';
import { isDirty } from '@/lib/cardNotes/cardNotesController';
import { useAuthStore } from '@/stores/authStore';

const VENUES: readonly CardVenue[] = ['kalshi', 'polymarket'];

/** One retry of a failed (not refused) scope read, after this long. */
export const SCOPE_RETRY_MS = 5000;

/** A transport failure or a 5xx: the relay was away, not a verdict on this account. Never a 403. */
const isTransient = (error: unknown) =>
  error instanceof CardAnnotationClientError
  && (error.code === 'CARD_CLIENT_UNAVAILABLE' || (error.status != null && error.status >= 500));

export function TrackerAnnotationProviders({
  children,
  transport = browserCardAnnotationTransport,
  retryMs = SCOPE_RETRY_MS,
}: {
  children: ReactNode;
  transport?: CardAnnotationTransport;
  /** Delay before the one retry of a failed scope read (tests shorten it). */
  retryMs?: number;
}) {
  // The same fields that advance the auth session epoch.
  const identity = useAuthStore((s) => `${s.user?.id ?? ''}|${s.user?.orgId ?? ''}|${s.user?.role ?? ''}|${s.status === 'unauthenticated'}`);
  const [scope, setScope] = useState<string | null>(null);
  const [seen, setSeen] = useState(identity);
  // `pending`: a refusal wants a re-read. `reading`: the latest scope read is in
  // flight. `settled` counts finished reads, so the bridge re-evaluates when one
  // ends: a refusal that lands mid-read runs one more read then, never lost.
  const [pending, setPending] = useState(false);
  const [settled, setSettled] = useState(0);
  const reading = useRef(false);
  if (seen !== identity) {
    // Adjust state during render: Alice's scope is gone before Bob's first paint.
    setSeen(identity);
    setScope(null);
    setPending(false); // the identity read below covers anything Alice's refusals wanted
  }

  const identityRef = useRef(identity);
  // Layout effects run before the passive effect that starts the read.
  useLayoutEffect(() => { identityRef.current = identity; }, [identity]);
  const sequence = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef<() => void>(() => {});
  const retried = useRef(false);
  const onRefused = useCallback(() => setPending(true), []);

  // Built once, like the providers' adapters: the transport is fixed for the
  // component's lifetime, so the adapters and the availability channel the
  // providers connect to can never come from different builds.
  const [{ client, notes, sources, availability }] = useState(() => {
    const channel = createCardAvailabilityChannel();
    const wrapped = watchRefusals(createCardAnnotationClient(transport), () => onRefused());
    return {
      client: wrapped,
      availability: channel,
      notes: createCardNoteAdapter(wrapped, channel.options),
      sources: createCardSourceAdapter(wrapped, channel.options),
    };
  });

  const refresh = useCallback(() => {
    if (!useAuthStore.getState().user) return; // signed out: no account to resolve, nothing to ask
    const mine = ++sequence.current;
    const startedFor = identityRef.current;
    reading.current = true;
    client.readScope(VENUES).then(
      (result) => {
        if (mine !== sequence.current || startedFor !== identityRef.current) return;
        reading.current = false;
        retried.current = false;
        setSettled((n) => n + 1);
        setScope(pageScopeFor(result, VENUES));
      },
      (error) => {
        if (mine !== sequence.current || startedFor !== identityRef.current) return;
        reading.current = false;
        setSettled((n) => n + 1);
        if (error instanceof CardAnnotationClientError && error.code === 'CARD_CLIENT_CONTEXT_CHANGED') {
          // The auth epoch moved but the identity string did not (two changes batched into
          // one render, e.g. A to X to A). The scope is still this identity's own; read again.
          setPending(true);
          return;
        }
        setScope(null);
        if (isTransient(error) && !retried.current) {
          // The relay was away (restart, timeout): a disabled page can never produce the refusal
          // that would re-read, so try once more. Same guards; never after a 403.
          retried.current = true;
          retryTimer.current = setTimeout(() => {
            retryTimer.current = null;
            if (mine === sequence.current && startedFor === identityRef.current) refreshRef.current();
          }, retryMs);
        }
      },
    );
  }, [client, retryMs]);

  useLayoutEffect(() => { refreshRef.current = refresh; }, [refresh]);

  // Mount and identity trigger.
  useEffect(() => {
    retried.current = false;
    refresh();
    return () => {
      sequence.current += 1;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = null;
    };
  }, [refresh, identity]);

  const run = useCallback(() => { setPending(false); refresh(); }, [refresh]);

  return (
    <CardNotesProvider adapter={notes} scope={scope} availability={availability}>
      <CardSourcesProvider adapter={sources} scope={scope} availability={availability}>
        <RefusalBridge pending={pending} settled={settled} reading={reading} onRun={run} />
        {children}
      </CardSourcesProvider>
    </CardNotesProvider>
  );
}

/** Runs a pending re-read once no note draft is dirty. Lives inside the notes provider to read its store. */
function RefusalBridge({ pending, settled, reading, onRun }: { pending: boolean; settled: number; reading: { current: boolean }; onRun: () => void }) {
  const notes = useCardNotes();
  const dirty = useStore(notes!.controller.store, (s) => isDirty(s.editor));
  useEffect(() => {
    if (pending && !dirty && !reading.current) onRun();
  }, [pending, dirty, settled, reading, onRun]);
  return null;
}
